import { useMemo, useState } from 'react';
import { autoFillPlan, calculate } from '../lib/calc';
import { fmtCeil, fmtPct } from '../lib/format';
import type { Category, PatternResult, PlanInputs } from '../types';

interface Props {
  baseInputs: PlanInputs;
  onApply: (patch: Partial<PlanInputs>) => void;
}

interface PatternDef {
  label: string;
  description: string;
  category: Category;
  targetWageDiff: number;
}

const PATTERNS: PatternDef[] = [
  {
    label: 'パターン①',
    description: '区分A・加点希望なし（成長率1.0〜1.5%、加点取得なし）',
    category: 'A_no',
    targetWageDiff: 0,
  },
  {
    label: 'パターン②',
    description: '区分A・加点希望あり（CAGR 3.1〜3.2% + +30円段階）',
    category: 'A_yes',
    targetWageDiff: 30,
  },
  {
    label: 'パターン③',
    description: '区分A・加点希望あり（CAGR 3.1〜3.2% + +50円段階・最高加点）',
    category: 'A_yes',
    targetWageDiff: 50,
  },
  {
    label: 'パターン④B',
    description: '区分B・既受給（CAGR 3.5〜3.6% + +50円段階）',
    category: 'B',
    targetWageDiff: 50,
  },
];

function buildPattern(base: PlanInputs, def: PatternDef): PatternResult {
  const auto = autoFillPlan(
    def.category,
    base.prevSalary,
    base.prevEmployees,
    base.emp1,
    base.emp2,
    base.emp3
  );
  const targetSiteMin = base.regionMin + def.targetWageDiff;
  const inputs: PlanInputs = {
    ...base,
    category: def.category,
    siteMin: targetSiteMin,
    salary1: auto.salary1,
    salary2: auto.salary2,
    salary3: auto.salary3,
  };
  return {
    label: def.label,
    description: def.description,
    category: def.category,
    targetWageDiff: def.targetWageDiff,
    inputs,
    result: calculate(inputs),
  };
}

export function PatternComparison({ baseInputs, onApply }: Props) {
  const patterns = useMemo(() => PATTERNS.map((def) => buildPattern(baseInputs, def)), [baseInputs]);
  const [appliedLabel, setAppliedLabel] = useState<string | null>(null);

  const handleApply = (p: PatternResult) => {
    onApply({
      category: p.category,
      siteMin: p.inputs.siteMin,
      salary1: p.inputs.salary1,
      salary2: p.inputs.salary2,
      salary3: p.inputs.salary3,
    });
    setAppliedLabel(p.label);
    // システム入力時の参考値セクションへスクロール
    setTimeout(() => {
      const target = document.getElementById('system-values');
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="section">
      <h2>
        <span className="num">7</span>複数パターン並列比較
      </h2>
      <p className="section-desc">
        前年度実績・従業員数推移・所在地はそのままで、<b>区分・賃金加点段階</b>を変えた場合の必要計画値を並べます。
      </p>

      <div className="tip-box">
        <span className="lbl">
          <i className="fas fa-arrow-down" /> 「採用」ボタンの動作：
        </span>
        押すと、選択した区分・計画値・事業場内最低賃金が自動入力され、
        <b>下の「⑥ システム入力時の参考値」セクションに反映</b>されます。
        押した直後に該当セクションへ自動スクロールします。
      </div>

      {appliedLabel && (
        <div className="judge-box ok" style={{ marginTop: '0.6rem' }}>
          <div className="ttl">
            <i className="fas fa-check-circle" /> {appliedLabel} を採用しました
          </div>
          <div className="desc">
            計画値・事業場内最低賃金が更新されました。下のセクション⑥「システム入力時の参考値」を確認してください。
          </div>
        </div>
      )}

      <div className="table-wrap">
        <table className="plan-table compare">
          <thead>
            <tr>
              <th>パターン</th>
              <th>区分／加点</th>
              <th>推奨レンジ</th>
              <th>3年度目<br />給与支給総額</th>
              <th>3年度目<br />1人当給与</th>
              <th>事業場内<br />最低賃金（推定）</th>
              <th>実質取得加点</th>
              <th>採用</th>
            </tr>
          </thead>
          <tbody>
            {patterns.map((p) => (
              <tr key={p.label}>
                <td className="lbl-cell">{p.label}</td>
                <td>
                  <small>{p.description}</small>
                </td>
                <td className="auto">
                  <small>
                    {fmtPct(p.result.cagr1, 1)}〜{fmtPct(p.result.cagr3, 1)}%
                  </small>
                </td>
                <td className="auto">{fmtCeil(p.inputs.salary3)} 円</td>
                <td className="auto">{fmtCeil(p.result.targetPerEmp3)} 円</td>
                <td className="auto">{fmtCeil(p.inputs.siteMin)} 円</td>
                <td className="auto">
                  <small>{p.result.effective}</small>
                </td>
                <td>
                  <button
                    className="btn btn-mini btn-purple"
                    onClick={() => handleApply(p)}
                    title="クリックで⑥システム入力時の参考値に反映"
                  >
                    採用 →⑥
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
